import { useEffect, useRef } from "react";

/**
 * Attach IntersectionObserver to all elements matching `selector`.
 * When an element enters the viewport, the `visible` class is added.
 *
 * Usage:
 *   useScrollAnimation('.fade-target')
 *   // then in CSS:
 *   .fade-target { opacity: 0; transform: translateY(20px); transition: ... }
 *   .fade-target.visible { opacity: 1; transform: none; }
 */
export default function useScrollAnimation(
  selector = ".scroll-reveal",
  options = {},
) {
  const observerRef = useRef(null);

  useEffect(() => {
    const { threshold = 0.1, rootMargin = "0px 0px -60px 0px" } = options;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            // Once visible, stop observing (fire once)
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin },
    );

    const elements = document.querySelectorAll(selector);
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, [selector]);
}
